import { styled } from "goober";
import { useMemo, useState } from "react";
import { isDraft } from "../../../shared/connectors/isDraft";
import { VISITOR_CONNECTOR_GRANTS_FLAG } from "../../../shared/featureFlagNames";
import { getConnectorStatus } from "../../lib/connectorStatus";
import { queries } from "../../../shared/queries";
import { useConnectorCreator } from "../../hooks/useConnectorCreator";
import { useFocusShortcut } from "../../hooks/useFocusShortcut";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "@rocicorp/zero/react";
import { connectorMatchesQuery } from "../../lib/connector";
import {
  connectorDirectory,
  recommendedCustomConnectors,
} from "../../lib/connectorDirectory";
import {
  useMakeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../lib/urls";
import { Backdrop } from "../Backdrop";
import { Button } from "../Button";
import { ClearButton } from "../ClearButton";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { CountBadge } from "../CountBadge";
import { Dropdown } from "../Dropdown";
import { FacePile } from "../FacePile";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { Tab, TabHeaderBar, Tabs } from "../TabHeader";
import { Text } from "../Text";
import { TabNudge } from "./TabNudge";
import { ConnectedAccountsButton } from "./ConnectedAccountsButton";

const directoryCount = connectorDirectory.length;

const Container = styled("div")`
  flex: 1;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled("div")`
  display: flex;
  flex-direction: column;
`;

const SearchContainer = styled("div")`
  position: relative;
`;

const HeaderActions = styled("div")`
  margin-left: auto;
  margin-right: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled("div")`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-primary);
  pointer-events: none;
`;

type StatusDotColor = "orange" | "green" | "red" | null;

const StatusDotContainer = styled("div")`
  position: absolute;
  top: -2px;
  right: -2px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--tab-background);
`;

const StatusDot = styled("div")`
  width: 5px;
  height: 5px;
  border-radius: 50%;
`;

const POPULAR_STACK_COUNT = 8;
const POPULAR_BADGE_SIZE = 24;
// Pin the quick-create menu width so it hugs the badge stack.
const POPULAR_STACK_MENU_WIDTH = 196;

// A rotating stack of featured connector logos, reshuffled each time the menu
// opens — a visual entry to the library, in the standard avatar-pile style.
function PopularConnectorStack() {
  const [stackApps] = useState(() =>
    connectorDirectory
      .filter((app) => app.tags.includes("featured"))
      .sort(() => Math.random() - 0.5)
      .slice(0, POPULAR_STACK_COUNT)
  );

  return (
    <FacePile
      itemSize={POPULAR_BADGE_SIZE}
      maxItems={stackApps.length}
      outlineWidth={2}
      cutout
      items={stackApps.map((app) => ({
        id: app.identifier,
        label: app.name,
        bare: true,
        node: (
          <ConnectorLogoContainer
            size={POPULAR_BADGE_SIZE}
            outline={false}
            outlineWidth={2}
            background
            hoverBackground={false}
          >
            <ConnectorLogo
              template={{ name: app.name, identifier: app.identifier }}
              size={12}
            />
          </ConnectorLogoContainer>
        ),
      }))}
    />
  );
}

type Props = {
  children: React.ReactNode;
  headerItem?: React.ReactNode;
};

export function ConnectorsLayout({ children, headerItem }: Props) {
  const makeUrl = useMakeUrl();
  const [isConnected] = useRouteMatch(routes.connectors.connected);
  const [isDirectory] = useRouteMatch(routes.connectors.library);
  const connectedUrl = useCurrentUrl(routes.connectors.connected);
  const directoryUrl = useCurrentUrl(routes.connectors.library);
  const query = (isDirectory ? directoryUrl.q : connectedUrl.q) ?? "";
  const searchRef = useFocusShortcut<HTMLInputElement>();
  const canCreateApp = useHasAccess("tenant:connected_app_create");
  const visitorGrantsEnabled = useFeatureFlag(VISITOR_CONNECTOR_GRANTS_FLAG);
  const createConnector = useConnectorCreator(() => {});

  const [connectors, connectorsDetails] = useQuery(queries.connectorsIndex());
  const { justCreated } = useCurrentUrl(routes.connectors.library);
  const justCreatedApp = justCreated
    ? connectors.find((app) => app.id === justCreated)
    : undefined;

  const count = connectors.length;

  const connectedMatchCount = useMemo(() => {
    if (!query) return connectors.length;
    const lower = query.toLowerCase();
    return connectors.filter((a) => connectorMatchesQuery(a, lower)).length;
  }, [connectors, query]);

  const directoryMatchCount = useMemo(() => {
    if (!query) return directoryCount;
    const lower = query.toLowerCase();
    return connectorDirectory.filter((a) =>
      a.name.toLowerCase().includes(lower)
    ).length;
  }, [query]);

  const tabJumpTarget: "directory" | "connected" | null =
    query.length >= 2 && connectorsDetails.type === "complete"
      ? isConnected && connectedMatchCount === 0 && directoryMatchCount > 0
        ? "directory"
        : isDirectory && connectedMatchCount > 0
          ? "connected"
          : null
      : null;

  let statusDotColor: StatusDotColor = null;
  if (justCreatedApp) {
    if (isDraft(justCreatedApp)) {
      statusDotColor = "orange";
    } else {
      const status = getConnectorStatus(justCreatedApp);
      statusDotColor =
        status === "error" ? "red" : status === "active" ? "green" : null;
    }
  }

  return (
    <Backdrop permanentNavigation>
      <Container>
        <Header>
          <TabHeaderBar>
            <Tabs>
              <TabNudge
                show={tabJumpTarget === "connected"}
                count={connectedMatchCount}
                trigger={({ style: anchorStyle }) => (
                  <Tab
                    to={makeUrl(routes.connectors.connected, { q: query })}
                    className={isConnected ? "active" : ""}
                    style={anchorStyle}
                  >
                    <Icon name="api-key-custom" size={16} />
                    <Text variant="text-small-md">Connectors</Text>

                    {connectorsDetails.type === "complete" && (
                      <CountBadge style={{ position: "relative" }}>
                        <Text variant="text-smaller-md">{count}</Text>
                        {statusDotColor && (
                          <StatusDotContainer>
                            <StatusDot
                              style={{
                                background: `var(--${statusDotColor}-600)`,
                              }}
                            />
                          </StatusDotContainer>
                        )}
                      </CountBadge>
                    )}
                  </Tab>
                )}
              />
              <TabNudge
                show={tabJumpTarget === "directory"}
                count={directoryMatchCount}
                trigger={({ style: anchorStyle }) => (
                  <Tab
                    to={makeUrl(routes.connectors.library, { q: query })}
                    className={isDirectory ? "active" : ""}
                    style={anchorStyle}
                  >
                    <Icon name="store" size={16} />
                    <Text variant="text-small-md">Browse library</Text>
                    <CountBadge>
                      <Text variant="text-smaller-md">
                        {directoryCount.toLocaleString("en-US")}
                      </Text>
                    </CountBadge>
                  </Tab>
                )}
              />
            </Tabs>
            <HeaderActions>
              {visitorGrantsEnabled && <ConnectedAccountsButton />}
              <SearchContainer>
                <SearchIcon>
                  <Icon name="search" size={12} />
                </SearchIcon>
                <Input
                  ref={searchRef}
                  data-search
                  placeholder="Search…"
                  value={query}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && tabJumpTarget) {
                      e.preventDefault();
                      if (tabJumpTarget === "directory") {
                        navigate(routes.connectors.library, {
                          q: query,
                          tag: "all",
                        });
                      } else {
                        navigate(routes.connectors.connected, { q: query });
                      }
                    }
                  }}
                  onChange={(e) => {
                    if (isDirectory) {
                      navigate(
                        routes.connectors.library,
                        { q: e.target.value, tag: "all" },
                        { replace: true }
                      );
                    } else {
                      navigate(
                        routes.connectors.connected,
                        { q: e.target.value },
                        { replace: true }
                      );
                    }
                  }}
                />
                {query && (
                  <ClearButton
                    onPress={() => {
                      if (isDirectory) {
                        navigate(
                          routes.connectors.library,
                          { q: "", tag: "all" },
                          { replace: true }
                        );
                      } else {
                        navigate(
                          routes.connectors.connected,
                          { q: "" },
                          { replace: true }
                        );
                      }
                      searchRef.current?.focus();
                    }}
                  >
                    <Icon name="x" size={12} />
                  </ClearButton>
                )}
              </SearchContainer>
              {canCreateApp && (
                <Dropdown
                  align="bottom"
                  minWidth={POPULAR_STACK_MENU_WIDTH}
                  maxWidth={POPULAR_STACK_MENU_WIDTH}
                  maxHeight="none"
                  opener={<Button size="l" icon="plus" />}
                  sections={[
                    {
                      label: `Browse ${directoryCount.toLocaleString(
                        "en-US"
                      )} connectors`,
                      items: [
                        {
                          type: "button" as const,
                          left: null,
                          label: <PopularConnectorStack />,
                          onClick: () => {
                            if (!isDirectory) {
                              navigate(routes.connectors.library, { q: "" });
                            }
                            // Focus the library search after its tab mounts.
                            // Deferred (a sync focus is stolen by the closing
                            // menu button / browser mousedown focus) and
                            // queried from the DOM (on a tab switch the input
                            // belongs to the newly mounted tab, not this one).
                            requestAnimationFrame(() =>
                              document
                                .querySelector<HTMLInputElement>(
                                  "[data-search]"
                                )
                                ?.focus()
                            );
                          },
                        },
                      ],
                    },
                    {
                      label: "Create custom connector",
                      items: [
                        ...recommendedCustomConnectors.map((app) => ({
                          type: "button" as const,
                          left: (
                            <ConnectorLogo
                              template={{
                                name: app.name,
                                identifier: app.identifier,
                              }}
                              size={16}
                            />
                          ),
                          label: app.name,
                          onClick: () => createConnector(app.identifier),
                        })),
                        {
                          type: "link" as const,
                          left: <Icon name="text-search" size={16} />,
                          label: "All custom connectors…",
                          to: makeUrl(routes.connectors.library, {
                            tag: "custom",
                            q: "",
                          }),
                        },
                      ],
                    },
                  ]}
                />
              )}
            </HeaderActions>
          </TabHeaderBar>
          {headerItem}
        </Header>

        {children}
      </Container>
    </Backdrop>
  );
}
